/**
 * useTranslationWithCMS — Translation hook with CMS tenant override support.
 *
 * WHAT: Wraps react-i18next's useTranslation with CMS-based key overrides.
 * WHY:  Tenants can customize specific UI strings via CMS entries without
 *       deploying code changes. CMS overrides take precedence over bundled
 *       locale files when available.
 * HOW:  Fetches CMS entry with type_key='i18n' and entry_key='overrides'
 *       for the current locale. Merges CMS keys over bundled translations.
 *
 * Usage:
 *   const { t } = useTranslationWithCMS();
 *   t('common.save')  // → "Save" (or CMS override if tenant has one)
 */

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';

/** CMS i18n override response shape */
interface CmsI18nOverrides {
  [key: string]: string;
}

/**
 * Fetch CMS i18n overrides for the current tenant + locale.
 * Returns flat key-value map (e.g. { "common.save": "Store" }).
 * Returns null if no overrides exist (404 is expected, not an error).
 */
async function fetchCmsOverrides(locale: string): Promise<CmsI18nOverrides | null> {
  try {
    const response = await api.get<{ data: { body: CmsI18nOverrides } }>(
      `/content/i18n/overrides`,
      { params: { locale } },
    );
    return response.data?.data?.body ?? null;
  } catch {
    // 404 = no overrides for this tenant/locale — expected, not an error
    return null;
  }
}

/**
 * Translation hook with CMS override support.
 *
 * @param ns - i18next namespace (default: 'translation')
 * @returns Standard i18next hook result with `t()` that checks CMS first
 */
export function useTranslationWithCMS(ns?: string) {
  const { t: i18nT, i18n, ready } = useTranslation(ns);
  const locale = i18n.language;

  // Fetch CMS overrides (stale-while-revalidate, 5 min cache)
  const { data: cmsOverrides } = useQuery({
    queryKey: ['cms-i18n-overrides', locale],
    queryFn: () => fetchCmsOverrides(locale),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false, // 404 is expected; don't retry
  });

  // Wrap t() to check CMS overrides first
  const t = useCallback(
    (key: string, options?: Record<string, unknown>) => {
      // Check CMS override first (flat dotted key, e.g. "common.save")
      if (cmsOverrides && typeof key === 'string' && key in cmsOverrides) {
        return cmsOverrides[key];
      }
      // Fall back to bundled locale
      return i18nT(key, options as never);
    },
    [cmsOverrides, i18nT],
  );

  return useMemo(
    () => ({ t, i18n, ready }),
    [t, i18n, ready],
  );
}
